<template>
  <div class="main-wrapper">
    <div class="page-wrapper">
      <div class="content container-fluid">
        <!-- Canvas: canh giữa & giới hạn max-width -->
        <div class="">

          <!-- Header / Toolbar -->
          <div class="card card-hero border-0 shadow-sm mb-4">
            <div class="card-body py-3">
              <div class="d-flex align-items-center justify-content-between flex-wrap">
                <div class="mb-2">
                  <h5 class="mb-1 font-weight-bold mb-0">
                    <i class="la la-file-alt mr-2"></i>Danh sách bài viết
                  </h5>
                  <small class="text-muted">Tìm kiếm · Lọc theo site · Chọn bước nhanh · Viết tiếp đúng bước hiện tại</small>
                </div>
                <div class="mb-2">
                  <button class="btn btn-success btn-pill" @click="goCreate">
                    <i class="la la-plus mr-1"></i> Tạo bài mới
                  </button>
                </div>
              </div>

              <!-- Filters -->
              <div class="mt-3">
                <div class="form-row align-items-end">
                  <div class="form-group col-xl-6 col-lg-6 col-md-7 mb-2">
                    <label class="small mb-1 text-muted">Từ khóa</label>
                    <div class="input-group input-group-merge">
                      <div class="input-group-prepend">
                        <span class="input-group-text bg-white border-right-0">
                          <i class="la la-search"></i>
                        </span>
                      </div>
                      <input
                        v-model.trim="keyword"
                        class="form-control border-left-0"
                        placeholder="mainKeyword / title / description..."
                        @keydown.enter.prevent="applyFilters"
                      />
                      <div class="input-group-append">
                        <button class="btn btn-outline-secondary" :disabled="loading || !keyword" @click="clearKeyword" title="Xóa tìm kiếm">
                          <i class="la la-times"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div class="form-group col-xl-3 col-lg-3 col-md-5 mb-2">
                    <label class="small mb-1 text-muted">Site</label>
                    <select v-model="siteId" class="custom-select">
                      <option value="">— Tất cả —</option>
                      <option v-for="s in sites" :key="s.id" :value="String(s.id)">
                        {{ s.name }} ({{ s.baseUrl }})
                      </option>
                    </select>
                  </div>

                  <div class="form-group col-xl-3 col-lg-3 col-md-12 mb-2 text-right">
                    <button class="btn btn-primary btn-pill mr-2" :disabled="loading" @click="applyFilters">
                      <span v-if="loading" class="spinner-border spinner-border-sm mr-1"></span>
                      <i v-else class="la la-filter mr-1"></i> Lọc
                    </button>
                    <button class="btn btn-outline-secondary btn-pill" :disabled="loading" @click="resetFilters">
                      <i class="la la-undo mr-1"></i> Reset
                    </button>
                  </div>
                </div>

                <!-- Step quick filter -->
                <ul class="nav nav-pills nav-pills-soft small mt-1 overflow-auto no-scrollbar">
                  <li class="nav-item mr-2 mb-1" v-for="s in stepOptions" :key="s.value">
                    <a
                      href="#"
                      class="nav-link px-3 py-1"
                      :class="step === s.value ? 'active' : ''"
                      @click.prevent="toggleStep(s.value)"
                      :title="s.full"
                    >
                      <i :class="['la', s.icon, 'mr-1']"></i>{{ s.short }}
                    </a>
                  </li>
                  <li class="nav-item mb-1">
                    <a href="#" class="nav-link px-3 py-1" :class="!step ? 'active' : ''" @click.prevent="toggleStep('')">
                      <i class="la la-asterisk mr-1"></i>Tất cả
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Table -->
          <div class="card border-0 shadow-sm">
            <div class="table-responsive table-modern table-sticky-header">
              <table class="table mb-0">
                <thead class="thead-light">
                  <tr class="text-uppercase small text-muted">
                    <th class="th-id">#ID</th>
                    <th>Tựa bài / Từ khóa</th>
                    <th class="th-site">Site</th>
                    <th class="th-progress">Tiến trình</th>
                    <th class="th-date">Cập nhật</th>
                    <th class="th-action text-right">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loading">
                    <td colspan="6" class="text-center py-5">
                      <span class="spinner-border spinner-border-sm mr-2"></span> Đang tải danh sách...
                    </td>
                  </tr>

                  <tr v-else-if="rows.length === 0">
                    <td colspan="6" class="text-center py-5">
                      <i class="la la-file-alt la-2x d-block mb-2 text-muted"></i>
                      <div class="text-muted">Không có bài viết phù hợp</div>
                    </td>
                  </tr>

                  <tr v-else v-for="a in rows" :key="a.id" class="row-hoverable">
                    <td class="align-middle">
                      <span class="badge badge-soft-dark">{{ a.id }}</span>
                    </td>

                    <td class="align-middle">
                      <div class="font-weight-600 text-truncate title-cell" :title="a.title || '[No title]'">
                        <span v-if="a.title">{{ a.title }}</span>
                        <span v-else class="text-muted">[No title]</span>
                      </div>
                      <div class="small text-muted mt-1">
                        <span v-if="a.mainKeyword" class="badge badge-soft-primary mr-1">
                          <i class="la la-key mr-1"></i>{{ a.mainKeyword }}
                        </span>
                        <span v-if="a.slug" class="badge badge-soft-secondary">
                          <i class="la la-link mr-1"></i>{{ a.slug }}
                        </span>
                      </div>
                    </td>

                    <td class="align-middle">
                      <div class="small text-truncate maxw-220" :title="siteName(a.selectedSiteId) || '—'">
                        <span v-if="a.selectedSiteId" class="chip">
                          <i class="la la-wordpress mr-1"></i>{{ siteName(a.selectedSiteId) || '—' }}
                        </span>
                        <span v-else class="text-muted">—</span>
                      </div>
                    </td>

                    <td class="align-middle">
                      <div class="d-flex align-items-center">
                        <span class="badge badge-pill mr-2" :class="stepBadgeClass(a.step)">
                          <i :class="['la', stepIcon(a.step), 'mr-1']"></i>{{ stepShort(a.step) }}
                        </span>
                        <div class="flex-fill">
                          <div class="progress progress-xxs rounded-pill">
                            <div class="progress-bar" :style="{ width: stepPercent(a.step) + '%' }"></div>
                          </div>
                        </div>
                      </div>
                    </td>

                    <td class="align-middle">
                      <div class="small text-muted">
                        <i class="la la-clock mr-1"></i>{{ formatDate(a.updatedAt) }}
                      </div>
                    </td>

                    <td class="align-middle text-right">
                      <!-- Luôn là 'Viết tiếp'; nếu PUSHED thì không cho bấm -->
                      <button
                        class="btn btn-sm btn-outline-primary btn-pill btn-elevate"
                        :disabled="a.step === 'PUSHED'"
                        @click="goContinue(a)"
                        :title="a.step === 'PUSHED' ? 'Bài đã PUSHED - không thể viết tiếp' : 'Mở đúng bước hiện tại để viết tiếp'"
                      >
                        <i class="la la-pen-nib mr-1"></i> Viết tiếp
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div class="card-body d-flex align-items-center justify-content-between">
              <div class="small text-muted">
                Trang <strong>{{ page + 1 }}</strong> / <strong>{{ totalPages || 1 }}</strong> · Tổng <strong>{{ totalElements }}</strong> bài
              </div>
              <div class="d-flex align-items-center">
                <select class="custom-select custom-select-sm mr-2 w-auto" v-model.number="size" @change="changeSize">
                  <option :value="10">10</option>
                  <option :value="20">20</option>
                  <option :value="50">50</option>
                </select>
                <div class="btn-group btn-group-sm" role="group">
                  <button class="btn btn-outline-secondary" :disabled="page===0 || loading" @click="prevPage" title="Trang trước">
                    <i class="la la-angle-left"></i>
                  </button>
                  <button class="btn btn-outline-secondary" :disabled="page>=totalPages-1 || loading" @click="nextPage" title="Trang sau">
                    <i class="la la-angle-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div> <!-- /page-canvas -->
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import api from '../api'

const router = useRouter()

/** ====== Filters state ====== */
const keyword = ref('')
const step = ref('')      // enum or ''
const siteId = ref('')

/** ====== List state ====== */
const rows = ref([])
const loading = ref(false)
const page = ref(0)           // 0-based
const size = ref(10)
const totalElements = ref(0)
const totalPages = ref(0)

/** ====== Sites for filter ====== */
const sites = ref([])

/** ====== Steps (khớp enum) ====== */
const stepOptions = [
  { value: 'INIT',               short: 'Init',        full: 'Khởi tạo',              icon: 'la-flag' },
  { value: 'SUB_KEYWORDS',       short: 'SubKW',       full: 'Sinh từ khóa phụ',       icon: 'la-list-ul' },
  { value: 'OUTLINE',            short: 'Outline',     full: 'Sinh dàn ý',             icon: 'la-sitemap' },
  { value: 'TITLE_DESCRIPTION',  short: 'Title/Desc',  full: 'Sinh tiêu đề & mô tả',   icon: 'la-heading' },
  { value: 'SEMANTIC_KEYWORDS',  short: 'Semantic',    full: 'Sinh semantic keywords', icon: 'la-project-diagram' },
  { value: 'CONTENT',            short: 'Content',     full: 'Sinh nội dung',          icon: 'la-file-alt' },
  { value: 'FINALIZED',          short: 'Final',       full: 'Hoàn tất nội dung',      icon: 'la-check-circle' },
  { value: 'PUSHED',             short: 'Pushed',      full: 'Đã đẩy lên WP',          icon: 'la-rocket' },
]

const stepShort = (s) => stepOptions.find(x => x.value === s)?.short || 'Init'
const stepIcon  = (s) => stepOptions.find(x => x.value === s)?.icon  || 'la-flag'
const stepBadgeClass = (s) => {
  switch (s) {
    case 'INIT': return 'badge-soft-secondary'
    case 'SUB_KEYWORDS': return 'badge-soft-info'
    case 'OUTLINE': return 'badge-soft-warning'
    case 'TITLE_DESCRIPTION': return 'badge-soft-primary'
    case 'SEMANTIC_KEYWORDS': return 'badge-soft-dark'
    case 'CONTENT': return 'badge-soft-success'
    case 'FINALIZED': return 'badge-soft-success'
    case 'PUSHED': return 'badge-soft-success'
    default: return 'badge-soft-secondary'
  }
}
const stepPercent = (s) => ({
  INIT: 5,
  SUB_KEYWORDS: 18,
  OUTLINE: 34,
  TITLE_DESCRIPTION: 50,
  SEMANTIC_KEYWORDS: 66,
  CONTENT: 82,
  FINALIZED: 92,
  PUSHED: 100
})[s] ?? 0

const stepEnumToWizardStep = (s) => ({
  INIT: 1,
  SUB_KEYWORDS: 2,
  OUTLINE: 3,
  TITLE_DESCRIPTION: 4,
  SEMANTIC_KEYWORDS: 5,
  CONTENT: 6,
  FINALIZED: 6,
  PUSHED: 6,
})[s] || 1

/** ====== Utils ====== */
const clearKeyword = () => { keyword.value = ''; applyFilters() }
const formatDate = (iso) => {
  if (!iso) return '—'
  const d = new Date(iso)
  const pad = (n) => (n < 10 ? '0' + n : n)
  return `${pad(d.getDate())}/${pad(d.getMonth()+1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}
const siteName = (id) => {
  const found = sites.value.find(s => String(s.id) === String(id))
  return found ? found.name : null
}

/** ====== Data load ====== */
const fetchSites = async () => {
  try {
    const res = await api.get('/api/wp-sites', { params: { page: 0, size: 100 } })
    sites.value = res?.data?.content || []
  } catch {
    sites.value = []
  }
}

const fetchList = async () => {
  loading.value = true
  try {
    const params = {
      keyword: keyword.value || undefined,
      step: step.value || undefined,
      siteId: siteId.value || undefined,
      page: page.value,
      size: size.value,
      sort: 'updatedAt,DESC',
    }
    // BE đã có endpoint gộp /api/articles/query
    const res = await api.get('/api/articles/query', { params })
    const p = res?.data
    rows.value = p?.content || []
    totalElements.value = p?.totalElements ?? 0
    totalPages.value = p?.totalPages ?? 0
  } catch {
    rows.value = []
    totalElements.value = 0
    totalPages.value = 0
  } finally {
    loading.value = false
  }
}

/** ====== Filter actions ====== */
const toggleStep = (val) => {
  step.value = val
  page.value = 0
  fetchList()
}
const applyFilters = () => {
  page.value = 0
  fetchList()
}
const resetFilters = () => {
  keyword.value = ''
  step.value = ''
  siteId.value = ''
  page.value = 0
  fetchList()
}

/** ====== Pagination ====== */
const changeSize = () => {
  page.value = 0
  fetchList()
}
const prevPage = () => {
  if (page.value > 0) {
    page.value -= 1
    fetchList()
  }
}
const nextPage = () => {
  if (page.value < totalPages.value - 1) {
    page.value += 1
    fetchList()
  }
}

/** ====== Navigation ====== */
const goCreate = () => router.push({ path: '/ai-writer' })
const goContinue = (row) => {
  if (row.step === 'PUSHED') return // phòng khi người dùng cố bấm
  const wizardStep = stepEnumToWizardStep(row.step)
  router.push({ path: `/ai-writer/${row.id}`, query: { step: String(wizardStep) } })
}

/** ====== Init ====== */
onMounted(async () => {
  await fetchSites()
  await fetchList()
})
</script>

<style scoped>
/* —— Canvas giữa —— */
.page-canvas { max-width: 1200px; margin: 0 auto; }

/* —— Hero —— */
.card-hero { background: #fff; border: 1px solid #edf0f2; }

/* —— Soft badges —— */
.badge-soft-primary   { background: rgba(0,123,255,.12); color:#007bff; }
.badge-soft-secondary { background: rgba(108,117,125,.12); color:#6c757d; }
.badge-soft-success   { background: rgba(40,167,69,.12); color:#28a745; }
.badge-soft-danger    { background: rgba(220,53,69,.12); color:#dc3545; }
.badge-soft-warning   { background: rgba(255,193,7,.18); color:#856404; }
.badge-soft-info      { background: rgba(23,162,184,.12); color:#17a2b8; }
.badge-soft-dark      { background: rgba(33,37,41,.12); color:#343a40; }

/* —— Pills —— */
.nav-pills-soft .nav-link{
  background:#f8f9fa;border:1px solid #edf0f2;color:#6c757d;border-radius:50rem;transition:all .15s ease
}
.nav-pills-soft .nav-link:hover{background:#f3f5f7}
.nav-pills-soft .nav-link.active{background:rgba(0,123,255,.08);color:#007bff;border-color:rgba(0,123,255,.2)}
.no-scrollbar{scrollbar-width:none}
.no-scrollbar::-webkit-scrollbar{display:none}

/* —— Table —— */
.table-modern .table{border-collapse:separate;border-spacing:0}
.table-modern thead th{
  position:sticky;top:0;z-index:1;background:#f8f9fa;border-top:none!important
}
.table td,.table th{vertical-align:middle;padding:.75rem 1rem}
.thead-light th{font-weight:600}
.row-hoverable:hover{background:#fcfdff}

/* Cột width cố định để thẳng hàng */
.th-id{width:96px}
.th-site{width:220px}
.th-progress{width:260px}
.th-date{width:180px}
.th-action{width:170px}

.title-cell{max-width:520px}
.maxw-220{max-width:220px}

/* —— Progress —— */
.progress-xxs{height:6px;background:#eef1f5}
.progress-xxs .progress-bar{background-color:#007bff}

/* —— Chip —— */
.chip{display:inline-flex;align-items:center;padding:.25rem .5rem;border-radius:50rem;background:#f1f3f5;color:#495057}

/* —— Buttons —— */
.btn-pill{border-radius:50rem}
.btn-elevate{box-shadow:0 1px 0 rgba(0,0,0,.02);transition:transform .05s}
.btn-elevate:active{transform:translateY(1px)}

/* —— Input merge —— */
.input-group-merge .input-group-prepend .input-group-text{border-right:0}
.input-group-merge .form-control{border-left:0}
</style>
